import React from 'react';
import { SlideOutCompProps } from '@moderepo/mode-app-components';
import { SlideOutPanelOptions } from '@moderepo/mode-ui-state';
import { ProjectTypeHandlersManager, ProjectTypeSlideOutCompHandler } from '../../utils/ritzExtensionUtils';
import { ChartMetricInfo, MultiDataSourcesExportDataOptionsSlideOutContainer, MultiDataSourcesViewOptionsSlideOutContainer } from '.';
import { ReadonlyNonEmptyArray } from '../../utils/utils';


/**
 * Interface for 1 export data format.
 */
export interface ExportDataFormat {
    // The internal name. This is the name that should be used when comparing/checking format.
    readonly format: string;

    // The file name extension of the format
    readonly fileExtension: string;

    // The name to be shown in the UI. This can be a translation key. If this key is not defined then the exact name will be used.
    readonly displayNameKey: string;
}


/**
 * The interface for configuring the MultiDataSourceViewContainer's view
 */
export interface MetricViewSettings {
    // Whether or not to show legends. The default is true unless this is specifically set to false.
    readonly showLegends?: boolean | undefined;

    // The maximum number of legends we want to show. If an entity has many metrics but we don't want to show the legends for all the metrics,
    // we can set a limit to have the component only show this number of legends. The default is undefined which mean no limit.
    readonly maxLegends?: number | undefined;

    // Maximum number of metrics we will select and show initially. If an entity has many metrics but we don't want to show them
    // all initially, we can set a limit. The component will only show this number of metrics initially. The user can select more metrics
    // to show once the component is loaded. The default is undefined means no limit.
    readonly maxInitiallySelected?: number | undefined;

    // Whether or not we allow the user to unselect the metrics. Default is true unless this is specifically set to false.
    readonly allowUnselect?: boolean | undefined;

    // Whether or not to hide metrics that don't have data. The default is TRUE unless this is specifically set to false. This will hide the legends
    // and the Y Axes and Series in the chart.
    readonly hideMetricsWithoutData?: boolean | undefined;
}



/**
 * Find the longest metric name so that we can compute the width needed to display the legends
 */
export const getLongestMetricNameInPixel = (
    numericMetrics: readonly ChartMetricInfo[], tagMetrics: readonly ChartMetricInfo[],
    showNumericMetricLegends?: boolean | undefined, showTagMetricLegends?: boolean | undefined,
    fontStyle?: string,
): number => {
    // Build the list of metrics that we will be displaying in the legends
    const metrics: ChartMetricInfo[] = [];

    // If we need to show legends for Numeric metrics then add the numeric metrics to the list
    if (showNumericMetricLegends !== false) {
        metrics.push(...numericMetrics);
    }

    // If we need to show legends for Tag metrics then add the tag metrics to the list
    if (showTagMetricLegends !== false) {
        metrics.push(...tagMetrics);
    }

    // Create a Cancel element which we can use for measuring a text's width
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context && fontStyle) {
        // We need to give the context the font style that is being used.
        context.font = fontStyle;
    }

    // Now find the metric that has the longest name
    const longestName = metrics.reduce((length, metric) => {
        const metricName = metric.displayName ?? metric.name ?? metric.id;
        // Use context to measure the width of the metricName. If context is not available, assume each letter is 20px.
        const metricNameWidthInPixel = context ? Math.ceil(context.measureText(metricName).width) : metricName.length * 20;
        return Math.max(length, metricNameWidthInPixel);
    }, 0);

    return longestName;
};



/**
 * The following logic is for setting up the slide out panels used by the MultiDataSourcesViewContainer. We have a place where we define all the
 * slide out panels exist in Ritz GLOBALLY in the "defaultProjectConfig.tsx". We can add these slide out panels globally so that they can be used
 * by other components HOWEVER, the UI for these slide out panels are too specific to MultiDataSourcesViewContainer that it doesn't make sense to
 * add them globally. Therefore, we will define and here. These slide out panels will still be registered with the global slide out panel system
 * however, we leave the implementation here so that whoever try to use these slide out panels can see why they are defined here but not in the
 * global "defaultProjectConfig.tsx".
 */


/**
 * The list of slide out panel types that are used by the MultiDataSourcesViewContainer
 * TODO - Add other type of slide out panels
 */
export enum MultiDataSourcesSlideOutPanelType {
    VIEW_OPTIONS = 'multi_data_source_view_options_panel',
    EXPORT_DATA_OPTIONS = 'multi_data_source_export_data_options_panel',
}


/**
 * The interface for the settings required to show the slide out panel used for asking the user to select metrics they want to view
 */
export interface MultiDataSourcesViewOptions extends SlideOutPanelOptions {
    readonly compType: MultiDataSourcesSlideOutPanelType.VIEW_OPTIONS;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly metrics: {
                readonly numericMetrics: readonly ChartMetricInfo[];
                readonly tagMetrics: readonly ChartMetricInfo[];
            }
            readonly selectedMetricIdsMap: {
                readonly numericMetrics: {
                    readonly [metricId: string]: boolean | undefined;
                };
                readonly tagMetrics: {
                    readonly [metricId: string]: boolean | undefined;
                };
            };
            readonly allowUnselectNumericMetrics?: boolean | undefined;
            readonly allowUnselectTagMetrics?: boolean | undefined;
            readonly onViewOptionsChanged: (options: {
                // The new map of selected metric ids. This will be undefined if the selections are not changed.
                readonly newSelectedMetricIdsMap: {
                    readonly numericMetrics: {
                        readonly [metricId: string]: boolean | undefined;
                    };
                    readonly tagMetrics: {
                        readonly [metricId: string]: boolean | undefined;
                    } ;
                } | undefined;
            })=> void;
        };
    }
}


/**
 * Type guard check to make sure the provided options is the options for MultiDataSourcesViewOptions
 */
const isMultiDataSourcesViewOptions = (options: unknown): options is MultiDataSourcesViewOptions => {
    return (options as MultiDataSourcesViewOptions)?.compType === MultiDataSourcesSlideOutPanelType.VIEW_OPTIONS;
};


/**
 * The interface for the settings required to show the slide out panel used for asking the user to select metrics they want to export
 */
export interface MultiDataSourcesExportDataCompOptions extends SlideOutPanelOptions {
    readonly compType: MultiDataSourcesSlideOutPanelType.EXPORT_DATA_OPTIONS;
    readonly compProps: {
        readonly data: {
            readonly projectId: number;
            readonly numericMetrics: readonly ChartMetricInfo[];
            readonly tagMetrics: readonly ChartMetricInfo[];
            readonly supportedDataFormats: ReadonlyNonEmptyArray<ExportDataFormat>;
            readonly onExportSelectedMetrics: (
                selectedDataFormat: ExportDataFormat,
                selectedFileName: string,
                selectedNumericMetrics: readonly ChartMetricInfo[],
                selectedTagMetrics: readonly ChartMetricInfo[],
            )=> void;
        };
    }
}



/**
 * Type guard check to make sure the provided options is the options for MultiDataSourcesExportDataCompOptions
 */
const isMultiDataSourcesExportDataCompOptions = (options: unknown): options is MultiDataSourcesExportDataCompOptions => {
    return (options as MultiDataSourcesExportDataCompOptions)?.compType === MultiDataSourcesSlideOutPanelType.EXPORT_DATA_OPTIONS;
};



/**
 * When we register the slide out panels with the ProjectTypeHandlersManager, we need to give it a ProjectTypeSlideOutCompHandler. The
 * ProjectTypeHandlersManager doesn't know how to create slide out panels therefore it depends on the ProjectTypeSlideOutCompHandler. Whenever
 * the ProjectTypeHandlersManager needs to create slide out panel for a given type, it will call the handler to create it. This is the
 * implementation of the ProjectTypeSlideOutCompHandler interface which has only 1 function, "getSlideOutComponent".
 */
const multiDataSourceViewSlideOutComponentsHandler: ProjectTypeSlideOutCompHandler = {

    getSlideOutComponent: (options: SlideOutPanelOptions): React.ReactElement<SlideOutCompProps> | undefined => {

        if (isMultiDataSourcesViewOptions(options)) {
            return (
                <MultiDataSourcesViewOptionsSlideOutContainer
                    projectId={options.compProps.data.projectId}
                    metrics={options.compProps.data.metrics}
                    selectedMetricIdsMap={options.compProps.data.selectedMetricIdsMap}
                    allowUnselectNumericMetrics={options.compProps.data.allowUnselectNumericMetrics}
                    allowUnselectTagMetrics={options.compProps.data.allowUnselectTagMetrics}
                    onViewOptionsChanged={options.compProps.data.onViewOptionsChanged}
                />
            );
        }

        if (isMultiDataSourcesExportDataCompOptions(options)) {
            return (
                <MultiDataSourcesExportDataOptionsSlideOutContainer
                    projectId={options.compProps.data.projectId}
                    supportedDataFormats={options.compProps.data.supportedDataFormats}
                    numericMetrics={options.compProps.data.numericMetrics}
                    tagMetrics={options.compProps.data.tagMetrics}
                    onExportSelectedMetrics={options.compProps.data.onExportSelectedMetrics}
                />
            );
        }

        // TODO - handle other type of slide out panels

        return undefined;
    },
};


/**
 * Register the slide out panel types and the handler that handle those types.
 */
ProjectTypeHandlersManager.getInstance().registerSlideOutComponentHandler(
    MultiDataSourcesSlideOutPanelType.VIEW_OPTIONS, multiDataSourceViewSlideOutComponentsHandler,
);
ProjectTypeHandlersManager.getInstance().registerSlideOutComponentHandler(
    MultiDataSourcesSlideOutPanelType.EXPORT_DATA_OPTIONS, multiDataSourceViewSlideOutComponentsHandler,
);
